import {
  ADMIN_ALL_USERS_API_URL,
  ADMIN_USER_API_URL,
  AUTH_ME_API_URL,
  AUTH_UPDATE_PROFILE_API_URL,
  STORAGE_KEYS,
} from '../core/config';

function readStoredToken() {
  try {
    return window.localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || '';
  } catch {
    return '';
  }
}

export function getAccessToken() {
  return readStoredToken();
}

export function setAuthSession({ accessToken, user }) {
  try {
    if (accessToken) {
      window.localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    }
    if (user) {
      window.localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    }
  } catch {
    /* ignore storage failures */
  }
}

export function clearAuthSession() {
  try {
    window.localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    window.localStorage.removeItem(STORAGE_KEYS.USER);
  } catch {
    /* ignore storage failures */
  }
}

async function parseJsonResponse(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      payload?.detail ||
      payload?.error_description ||
      payload?.error ||
      payload?.message ||
      `Request failed with status ${response.status}`;
    throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
  }
  return payload;
}

export async function loginWithFovero(email, password) {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const payload = await parseJsonResponse(response);
  return payload.data;
}

export async function updateProfileRequest(fullName) {
  const response = await fetch(AUTH_UPDATE_PROFILE_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: JSON.stringify({ full_name: fullName }),
  });
  const payload = await parseJsonResponse(response);
  return payload.data;
}

export async function fetchCurrentUser() {
  const token = readStoredToken();
  if (!token) return null;

  const response = await fetch(AUTH_ME_API_URL, {
    headers: authHeaders(),
  });
  if (response.status === 401) {
    clearAuthSession();
    return null;
  }
  const payload = await parseJsonResponse(response);
  return payload.data;
}

export async function fetchAllUsers() {
  const response = await fetch(ADMIN_ALL_USERS_API_URL, {
    headers: authHeaders(),
  });
  const payload = await parseJsonResponse(response);
  return payload.data || [];
}

export async function fetchAdminUserChatHistory(userId) {
  const uid = Number(userId);
  if (!Number.isFinite(uid)) {
    throw new Error('fetchAdminUserChatHistory requires a valid user_id');
  }
  const response = await fetch(`${ADMIN_USER_API_URL}/${uid}/chat/history`, {
    headers: authHeaders(),
  });
  const payload = await parseJsonResponse(response);
  return payload.data || [];
}

export async function fetchAdminUserChatSession(userId, sessionId) {
  const uid = Number(userId);
  const sid = sessionId != null ? String(sessionId).trim() : '';
  if (!Number.isFinite(uid) || !sid) {
    throw new Error('fetchAdminUserChatSession requires user_id and session_id');
  }
  const response = await fetch(
    `${ADMIN_USER_API_URL}/${uid}/chat/history/${encodeURIComponent(sid)}`,
    { headers: authHeaders() }
  );
  const payload = await parseJsonResponse(response);
  return payload.data ?? payload;
}

export async function deleteUserRequest(userId) {
  const response = await fetch(`${ADMIN_USER_API_URL}/${userId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  const payload = await parseJsonResponse(response);
  return payload.data;
}

export async function updateUserStatusRequest(userId, isActive) {
  const response = await fetch(`${ADMIN_USER_API_URL}/${userId}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: JSON.stringify({ is_active: isActive }),
  });
  const payload = await parseJsonResponse(response);
  return payload.data;
}

export function authHeaders() {
  const token = readStoredToken();
  if (!token) return {};
  const value = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  return { Authorization: value };
}
